import axios from "axios";
import { Success, Error } from "./Toast";

/**
 * Upload a single file to the server
 * @param {File} file - The file to upload
 * @param {Object} options - Upload configuration options
 * @returns {Promise<Object>}
 */
export const uploadFile = async (file, options = {}) => {
    const {
        allowedTypes = ["image/png", "image/jpeg", "image/webp", "application/pdf"],
        maxSizeInMB = 10,
        folder = "users"
    } = options;

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
        const allowedExtensions = allowedTypes
            .map(type => type.split("/")[1])
            .join(", ");

        throw new Error(
            `${file.name} is not a valid file format. Please upload ${allowedExtensions} files.`
        );
    }

    // Validate file size
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
        throw new Error(`File size must be less than ${maxSizeInMB}MB.`);
    }

    try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("folder", folder);

        const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/adminuser/upload`,
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${localStorage.getItem("Token")}`
                }
            }
        );

        if (response.data.Status === 1 || response.data.status === 1) {
            return {
                status: response.data.Status || response.data.status,
                message: response.data.Message || response.data.message,
                fileName: response.data.fileName || response.data.Filename,
                fileUrl: response.data.fileUrl || response.data.FileUrl
            };
        } else {
            throw new Error(response.data.Message || response.data.message || "Failed to upload file");
        }
    } catch (error) {
        console.error("Upload Error:", error);

        if (error.response?.data?.Message || error.response?.data?.message) {
            throw new Error(error.response.data.Message || error.response.data.message);
        } else if (error.message) {
            throw new Error(error.message);
        } else {
            throw new Error("Upload error. Please try again.");
        }
    }
};

/**
 * Upload multiple files sequentially
 * @param {File[]} files
 * @param {Object} options
 * @returns {Promise<Object[]>}
 */
export const uploadMultipleFiles = async (files, options = {}) => {
    const results = [];

    for (const file of files) {
        try {
            const result = await uploadFile(file, options);
            results.push(result);
        } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);

            results.push({
                status: 0,
                message: error.message,
                fileName: file.name,
                fileUrl: ""
            });
        }
    }

    return results;
};

export const useFileUpload = (options = {}) => {
    const uploadWithLoading = async (file, onSuccess, onError) => {
        try {
            const result = await uploadFile(file, options);

            const uploadedFile = {
                fileName: result.fileName,
                fileUrl: result.fileUrl,
                fileType: file.type
            };

            Success(`${file.name} uploaded successfully`);

            if (onSuccess) {
                onSuccess(uploadedFile);
            }

            return uploadedFile;
        } catch (error) {
            Error(error.message);

            if (onError) {
                onError(error.message);
            }

            return null;
        }
    };

    const uploadMultipleWithLoading = async (files, onSuccess, onError) => {
        const results = [];
        for (const file of files) {
            const result = await uploadWithLoading(file, null, null);
            if (result) {
                results.push(result);
            }
        }

        if (results.length > 0 && onSuccess) {
            onSuccess(results);
        } else if (results.length === 0 && onError) {
            onError("No files were uploaded successfully.");
        }

        return results;
    };

    return { uploadWithLoading, uploadMultipleWithLoading };
};



/**
 * Get file category based on MIME type
 * @param {string} fileType
 * @returns {'image' | 'pdf' | 'document' | 'other'}
 */
export const getFileCategory = (fileType) => {
    if (fileType.startsWith("image/")) {
        return "image";
    } else if (fileType === "application/pdf") {
        return "pdf";
    } else if (fileType.includes("document") || fileType.includes("text")) {
        return "document";
    } else {
        return "other";
    }
};

/**
 * Format file size into readable string
 * @param {number} bytes
 * @returns {string}
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
